import React, { useState, useEffect, useReducer, useContext, useMemo } from "react";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { LOCAL_TICKET_TRANSITION_EVENT } from "../../utils/ticketTransition";
import {
    shouldRefreshAutomationOnSafeRealtime,
    updateExistingTicketFromMessage
} from "../../utils/ticketListRealtime";

const useStyles = makeStyles((theme) => ({
    ticketsListWrapper: {
        position: "relative",
        display: "flex",
        height: "100%",
        flexDirection: "column",
        overflow: "hidden",
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
    },

    ticketsList: {
        flex: 1,
        maxHeight: "100%",
        overflowY: "scroll",
        ...theme.scrollbarStyles,
        borderTop: "2px solid rgba(0, 0, 0, 0.12)",
    },

    ticketsListHeader: {
        color: "rgb(67, 83, 105)",
        zIndex: 2,
        backgroundColor: "white",
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },

    ticketsCount: {
        fontWeight: "normal",
        color: "rgb(104, 121, 146)",
        marginLeft: "8px",
        fontSize: "14px",
    },

    noTicketsText: {
        textAlign: "center",
        color: "rgb(104, 121, 146)",
        fontSize: "14px",
        lineHeight: "1.4",
    },

    noTicketsTitle: {
        textAlign: "center",
        fontSize: "16px",
        fontWeight: "600",
        margin: "0px",
    },

    noTicketsDiv: {
        display: "flex",
        // height: "190px",
        margin: 40,
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },
}));

const ticketSortAsc = (a, b) => {
    
    if (a.updatedAt < b.updatedAt) {
        return -1;
    }
    if (a.updatedAt > b.updatedAt) {
        return 1;
    }
    return 0;
}

const ticketSortDesc = (a, b) => {
   
    if (a.updatedAt > b.updatedAt) {
        return -1;
    }
    if (a.updatedAt < b.updatedAt) {
        return 1;
    }
    return 0;
}

const reducer = (state, action) => {
    //console.log("action", action, state)
    const sortDir = action.sortDir;
    
    if (action.type === "LOAD_TICKETS") {
        const newTickets = action.payload;
        const nextState = action.replace ? [] : [...state];

        newTickets.forEach((ticket) => {
            const ticketIndex = nextState.findIndex((t) => t.id === ticket.id);
            if (ticketIndex !== -1) {
                nextState[ticketIndex] = ticket;
                if (ticket.unreadMessages > 0) {
                    nextState.unshift(nextState.splice(ticketIndex, 1)[0]);
                }
            } else {
                nextState.push(ticket);
            }
        });
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? nextState.sort(ticketSortAsc) : nextState.sort(ticketSortDesc);
        }

        return nextState;
    }

    if (action.type === "RESET_UNREAD") {
        const ticketId = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state[ticketIndex].unreadMessages = 0;
        }

        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET") {
        const ticket = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            // FIX PHOTO-MERGE: preserva dados do contato (ex: foto) quando o evento nao traz contato completo
            const _prevContact = state[ticketIndex].contact;
            state[ticketIndex] = {
                ...ticket,
                contact: ticket.contact ? { ..._prevContact, ...ticket.contact } : _prevContact
            };
        } else {
            state.unshift(ticket);
        }
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_EXISTING_TICKET") {
        const ticket = action.payload;
        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex === -1) return state;
        const previousContact = state[ticketIndex].contact;
        state[ticketIndex] = {
            ...ticket,
            contact: ticket.contact ? { ...previousContact, ...ticket.contact } : previousContact
        };
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }
        return [...state];
    }

    if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
        const ticket = action.payload;
        const nextState = updateExistingTicketFromMessage(state, ticket);
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? nextState.sort(ticketSortAsc) : nextState.sort(ticketSortDesc);
        }

        return nextState;
    }

    if (action.type === "UPDATE_TICKET_CONTACT") {
        const contact = action.payload;
        const ticketIndex = state.findIndex((t) => t.contactId === contact.id);
        if (ticketIndex !== -1) {
            state[ticketIndex].contact = contact;
        }
        return [...state];
    }

    if (action.type === "DELETE_TICKET") {
        const ticketId = action.payload;
        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state.splice(ticketIndex, 1);
        }

        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const TicketsListCustom = (props) => {
  const myAttendances = props.myAttendances || false;
    const {
        setTabOpen,
        status,
        searchParam,
        searchOnMessages,
        tags,
        kanbanTags,
        noKanban,
        noTag,
        users,
        showAll,
        selectedQueueIds,
        updateCount,
        countByTickets,
        countWithUnreadOnly,
        unreadFilter,
        style,
        whatsappIds,
        forceSearch,
        statusFilter,
        userFilter,
        sortTickets,
        filterAcrossStatuses
    } = props;

    const classes = useStyles();
    const [pageNumber, setPageNumber] = useState(1);
    const [socketRefresh, setSocketRefresh] = useState(0);
    let [ticketsList, dispatch] = useReducer(reducer, []);
    //   const socketManager = useContext(SocketContext);
    const { user, socket } = useContext(AuthContext);

    const { profile, queues } = user;
    const showTicketWithoutQueue = user.allTicket === 'enable' || user.allTicket === 'own';
    const companyId = user.companyId;

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [status, searchParam, dispatch, showAll, tags, kanbanTags, noKanban, noTag, users, forceSearch, selectedQueueIds, whatsappIds, statusFilter, sortTickets, searchOnMessages, filterAcrossStatuses]);

    const { tickets, hasMore, loading, unreadTicketCount } = useTickets({
        pageNumber,
        searchParam,
        status,
        showAll,
        searchOnMessages: searchOnMessages ? "true" : "false",
        tags: JSON.stringify(tags),
        kanbanTags: JSON.stringify(kanbanTags || []),
        noKanban: noKanban ? "true" : "false",
        noTag: noTag ? "true" : "false",
        users: JSON.stringify(users),
        queueIds: JSON.stringify(selectedQueueIds),
        whatsappIds: JSON.stringify(whatsappIds),
        statusFilter: JSON.stringify(statusFilter),
        userFilter,
        sortTickets,
        filterAcrossStatuses: filterAcrossStatuses ? "true" : "false",
        forceSearch: `${String(forceSearch)}:${socketRefresh}`
    });


    useEffect(() => {
        // const queueIds = queues.map((q) => q.id);
        // const filteredTickets = tickets.filter(
        //     (t) => queueIds.indexOf(t.queueId) > -1
        // );
        // const allticket = user.allTicket === 'enabled';
        // if (profile === "admin" || allTicket || allowGroup || allHistoric) {
        if (companyId) {
            dispatch({
                type: "LOAD_TICKETS",
                payload: tickets,
                replace: pageNumber === 1,
                status,
                sortDir: sortTickets
            });
        }
        // } else {
        //  dispatch({ type: "LOAD_TICKETS", payload: filteredTickets });
        // }

    }, [tickets, pageNumber, status, sortTickets, companyId]);

    useEffect(() => {
      if (!socket) return;
        // União das duas formas de vínculo (UserWhatsapps + vínculo direto),
        // sem duplicar e normalizado pra número -- usado tanto pro filtro de
        // grupo quanto pro filtro de ticket sem-fila "own".
        const getUserAllowedWhatsappIds = () => ([
            ...new Set([
                ...((user?.whatsapps || []).map(w => Number(w.id))),
                ...(user?.whatsappId ? [Number(user.whatsappId)] : [])
            ])
        ]);

        const shouldUpdateTicket = ticket => {
            const isGroupTicket = ticket?.status === "group" || ticket?.isGroup;
            const isOwner = Number(ticket?.userId) === Number(user?.id);

            // O administrador com allowGroup pode auditar todos os grupos da
            // empresa ao ativar o olhinho. Os demais seguem por conexao.
            if (isGroupTicket) {
                if (!user?.allowGroup) return false;
                if (profile === "admin" && showAll) return true;
                const allowedIds = getUserAllowedWhatsappIds();
                return allowedIds.length > 0 && allowedIds.includes(Number(ticket?.whatsappId));
            }

            // Ownership nao depende da fila atual. Um usuario que mudou de
            // fila conserva e continua atendendo seus tickets historicos.
            if (isOwner) return true;

            if (ticket?.userId && profile !== "admin" && !showAll) return false;

            // "own": ticket sem-fila só é visivel se a conexão dele for vinculada ao
            // usuário -- mesma regra usada acima pra grupos, aplicada aqui pra evitar
            // que o socket mostre ticket sem-fila de OUTRO vendedor em tempo real.
            let canSeeWithoutQueue = (isGroupTicket && user?.allowGroup) || status === "chatbot";
            if (!canSeeWithoutQueue && showTicketWithoutQueue) {
                if (user?.allTicket === 'own') {
                    const allowedIds = getUserAllowedWhatsappIds();
                    canSeeWithoutQueue = allowedIds.length > 0 && allowedIds.includes(Number(ticket?.whatsappId));
                } else {
                    canSeeWithoutQueue = true;
                }
            }
            return (profile === "admin" || !ticket?.userId || showAll) &&
                ((!ticket?.queueId && canSeeWithoutQueue) || selectedQueueIds.map(Number).includes(Number(ticket?.queueId)))
        }
        // FIX FILTRO-POR-ABA: checagem adicional (so exibicao/UX, nao mexe em permissao) -
        // garante que ticket que chega ao vivo so entra na lista se bater com o filtro ativo
        const matchesActiveFilter = (ticket, messageBody) => {
            if (Array.isArray(users) && users.length > 0 && !users.includes(ticket?.userId)) return false;
            if (Array.isArray(whatsappIds) && whatsappIds.length > 0 && !whatsappIds.includes(ticket?.whatsappId)) return false;
			const ticketTags = ticket?.tags || [];
			const ticketTagIds = ticketTags.map(tag => Number(tag.id));
			const ticketKanbanTagIds = ticketTags
				.filter(tag => Number(tag.kanban) === 1)
				.map(tag => Number(tag.id));
			const ticketRegularTagIds = ticketTags
				.filter(tag => Number(tag.kanban) !== 1)
				.map(tag => Number(tag.id));
			const contactTagIds = (ticket?.contact?.tags || []).map(tag => Number(tag.id));
			if (Array.isArray(tags) && tags.length > 0) {
				const matchesRegularTag = tags.some(tagId =>
					ticketTagIds.includes(Number(tagId)) || contactTagIds.includes(Number(tagId))
				);
				if (!matchesRegularTag) return false;
			}
			if (Array.isArray(kanbanTags) && kanbanTags.length > 0 &&
				!kanbanTags.some(tagId => ticketTagIds.includes(Number(tagId)))) return false;
			if ((noTag === true || noTag === "true") &&
				(ticketRegularTagIds.length > 0 || contactTagIds.length > 0)) return false;
			if ((noKanban === true || noKanban === "true") && ticketKanbanTagIds.length > 0) return false;
            if (searchParam) {
                const sp = String(searchParam).toLowerCase().trim();
                const name = (ticket?.contact?.name || "").toLowerCase();
                const number = (ticket?.contact?.number || "");
                const numericParam = String(searchParam).replace(/\D/g, "");
                const matchesName = sp && name.includes(sp);
                const matchesNumber = numericParam.length >= 4 && number.includes(numericParam);
                const matchesMessage = searchOnMessages && typeof messageBody === "string" &&
                    messageBody.toLowerCase().includes(sp);
                if (!matchesName && !matchesNumber && !matchesMessage) {
                    if (searchOnMessages && typeof messageBody !== "string") return null;
                    return false;
                }
            }
            return true;
        }
        // const shouldUpdateTicketUser = (ticket) =>
        //     selectedQueueIds.indexOf(ticket?.queueId) > -1 && (ticket?.userId === user?.id || !ticket?.userId);

        const notBelongsToUserQueues = (ticket) =>
            !(ticket?.status === "group" || ticket?.isGroup) &&
            ticket.queueId &&
            Number(ticket.userId) !== Number(user?.id) &&
            !selectedQueueIds.map(Number).includes(Number(ticket.queueId));

        const onCompanyTicketTicketsList = (data) => {
            if (status === "search" && filterAcrossStatuses && data.action === "update") {
                // A lista consolidada cruza status; a API reaplica coluna e
                // permissao sem depender do status anterior do ticket.
                setSocketRefresh(previous => previous + 1);
                return;
            }
            // console.log("onCompanyTicketTicketsList", data)
            if (data.action === "updateUnread") {
                dispatch({
                    type: "RESET_UNREAD",
                    payload: data.ticketId,
                    status: status,
                    sortDir: sortTickets
                });
            }
            // console.log(shouldUpdateTicket(data.ticket))
            const activeFilterMatch = data.action === "update" ? matchesActiveFilter(data.ticket) : false;
            if (data.action === "update" &&
                shouldUpdateTicket(data.ticket) && data.ticket.status === status && activeFilterMatch === true) {
                dispatch({
                    type: "UPDATE_TICKET",
                    payload: data.ticket,
                    status: status,
                    sortDir: sortTickets
                });
            }

            // FIX-ITEM31-TICKET-TRANSITION-UPDATE: o proprio update remove da
            // tela de quem perdeu acesso apos troca de atendente/fila.
            if (data.action === "update" && !shouldUpdateTicket(data.ticket)) {
                dispatch({
                    type: "DELETE_TICKET", payload: data.ticket?.id, status: status,
                    sortDir: sortTickets
                });
            }
			if (data.action === "update" && data.ticket.status === status && activeFilterMatch === false) {
				dispatch({
					type: "DELETE_TICKET", payload: data.ticket?.id, status: status,
					sortDir: sortTickets
				});
			}
            if (data.action === "update" &&
                shouldUpdateTicket(data.ticket) && data.ticket.status === status && activeFilterMatch === null) {
                dispatch({
                    type: "UPDATE_EXISTING_TICKET",
                    payload: data.ticket,
                    status: status,
                    sortDir: sortTickets
                });
            }

            // Se ticket mudou de status e nao pertence mais a esta aba, remover
            if (data.action === "update" && data.ticket.status !== status) {
                dispatch({
                    type: "DELETE_TICKET", payload: data.ticket?.id, status: status,
                    sortDir: sortTickets
                });
            }

            if (data.action === "update" && notBelongsToUserQueues(data.ticket)) {
                dispatch({
                    type: "DELETE_TICKET", payload: data.ticket?.id, status: status,
                    sortDir: sortTickets
                });
            }

            if (data.action === "delete") {
                dispatch({
                    type: "DELETE_TICKET", payload: data?.ticketId, status: status,
                    sortDir: sortTickets
                });

            }
        };

        const onCompanyAppMessageTicketsList = (data) => {
            if (data.action === "create" &&
                shouldUpdateTicket(data.ticket) && data.ticket.status === status &&
                matchesActiveFilter(data.ticket, data.message?.body) === true) {
                // Garantir que lastMessage esteja atualizado com o corpo da nova mensagem
                const updatedTicket = {
                    ...data.ticket,
                    lastMessage: data.message?.body || data.ticket.lastMessage,
                    updatedAt: new Date().toISOString(),
                };
                dispatch({
                    type: "UPDATE_TICKET_UNREAD_MESSAGES",
                    payload: updatedTicket,
                    status: status,
                    sortDir: sortTickets
                });
                if (countWithUnreadOnly) {
                    setSocketRefresh(previous => previous + 1);
                }
            }
        };

        const onCompanyContactTicketsList = (data) => {
            if (data.action === "update" && data.contact) {
                dispatch({
                    type: "UPDATE_TICKET_CONTACT",
                    payload: data.contact,
                    status: status,
                    sortDir: sortTickets
                });
            }
        };

        const onTicketScopeRefresh = () => {
            setSocketRefresh(previous => previous + 1);
        };

        const onKanbanRefreshTicketsList = () => {
            if (shouldRefreshAutomationOnSafeRealtime(status)) {
                setSocketRefresh(previous => previous + 1);
            }
        };

        const onConnectTicketsList = () => {
            if (status) {
                socket.emit("joinTickets", status);
            } else {
                socket.emit("joinNotification");
            }

            // ITEM37-REALTIME-RECOVERY: eventos ocorridos durante uma queda nao
            // sao reproduzidos pelo Socket.IO; reconciliar a lista via API.
            setSocketRefresh(previous => previous + 1);
        }

        const onRealtimeResume = () => {
            if (document.visibilityState && document.visibilityState !== "visible") return;
            socket.connect();
            onConnectTicketsList();
        };

        // Join room immediately on mount (not just on connect event)
        if (socket.connected) {
            onConnectTicketsList();
        }

        socket.on("connect", onConnectTicketsList)
        socket.on(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
        socket.on(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
        socket.on(`company-${companyId}-ticketRefresh`, onTicketScopeRefresh);
        socket.on(`company-${companyId}-kanbanRefresh`, onKanbanRefreshTicketsList);
        socket.on(`company-${companyId}-contact`, onCompanyContactTicketsList);
        window.addEventListener("online", onRealtimeResume);
        document.addEventListener("visibilitychange", onRealtimeResume);

        return () => {
            if (status) {
                socket.emit("leaveTickets", status);
            } else {
                socket.emit("leaveNotification");
            }
            socket.off("connect", onConnectTicketsList);
            socket.off(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
            socket.off(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
            socket.off(`company-${companyId}-ticketRefresh`, onTicketScopeRefresh);
            socket.off(`company-${companyId}-kanbanRefresh`, onKanbanRefreshTicketsList);
            socket.off(`company-${companyId}-contact`, onCompanyContactTicketsList);
            window.removeEventListener("online", onRealtimeResume);
            document.removeEventListener("visibilitychange", onRealtimeResume);
        };

    }, [status, showAll, user, selectedQueueIds, tags, kanbanTags, noTag, noKanban, users, profile, queues, sortTickets, showTicketWithoutQueue, searchParam, whatsappIds, filterAcrossStatuses]);

    useEffect(() => {
        // Rede de seguranca para uma perda isolada de pacote, suspensao do
        // navegador ou troca de rede. O socket continua sendo o caminho
        // imediato; esta reconciliacao visivel impede estado obsoleto eterno.
        const intervalId = window.setInterval(() => {
            if (document.visibilityState === "visible" && navigator.onLine !== false) {
                setSocketRefresh(previous => previous + 1);
            }
        }, 15000);
        return () => window.clearInterval(intervalId);
    }, [companyId]);

    useEffect(() => {
        const onLocalTicketRead = (event) => {
            const ticketId = event?.detail?.ticketId;
            if (!ticketId) return;
            dispatch({
                type: "RESET_UNREAD",
                payload: ticketId,
                status: status,
                sortDir: sortTickets
            });
            if (countWithUnreadOnly) {
                setSocketRefresh(previous => previous + 1);
            }
        };
        window.addEventListener("estratiza-ticket-read", onLocalTicketRead);
        return () => {
            window.removeEventListener("estratiza-ticket-read", onLocalTicketRead);
        };
    }, [status, sortTickets, countWithUnreadOnly]);

    useEffect(() => {
        const onLocalTicketTransition = (event) => {
            const transitionedTicket = event?.detail?.ticket;
            if (!transitionedTicket?.id) return;

            // Remove imediatamente da lista de origem. O refresh traz o ticket
            // para a lista de destino aplicando os mesmos filtros/permissoes da API.
            dispatch({
                type: "DELETE_TICKET",
                payload: transitionedTicket.id,
                status,
                sortDir: sortTickets
            });
            setSocketRefresh(previous => previous + 1);
        };

        window.addEventListener(LOCAL_TICKET_TRANSITION_EVENT, onLocalTicketTransition);
        return () => {
            window.removeEventListener(LOCAL_TICKET_TRANSITION_EVENT, onLocalTicketTransition);
        };
    }, [status, sortTickets]);

    useEffect(() => {
        if (typeof updateCount === "function") {
            updateCount(countByTickets ? ticketsList.length : countWithUnreadOnly ? unreadTicketCount : ticketsList.reduce((acc, t) => acc + (t.unreadMessages || 0), 0));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketsList, unreadTicketCount]);

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;

        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    if (status && status !== "search") {
        ticketsList = ticketsList.filter(ticket => ticket.status === status)
    }
    if (unreadFilter) {
        ticketsList = ticketsList.filter(t => (t.unreadMessages || 0) > 0);
    }

    return (
        <Paper className={classes.ticketsListWrapper} style={style}>
            <Paper
                square
                name="closed"
                elevation={0}
                className={classes.ticketsList}
                onScroll={handleScroll}
            >
                <List style={{ paddingTop: 0 }} >
                    {ticketsList.length === 0 && !loading ? (
                        <div className={classes.noTicketsDiv}>
                            <span className={classes.noTicketsTitle}>
                                {i18n.t("ticketsList.noTicketsTitle")}
                            </span>
                            <p className={classes.noTicketsText}>
                                {i18n.t("ticketsList.noTicketsMessage")}
                            </p>
                        </div>
                    ) : (
                        <>
                            {ticketsList.map((ticket) => (
                                // <List key={ticket.id}>
                                //     {console.log(ticket)}
                                <TicketListItem
                                    ticket={ticket}
                                    key={ticket.id}
                                    setTabOpen={setTabOpen}
                                    myAttendances={myAttendances}
                                />
                                // </List>
                            ))}
                        </>
                    )}
                    {loading && <TicketsListSkeleton />}
                </List>
            </Paper>
        </Paper>
    );
};

export default TicketsListCustom;
